import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { io, type Socket } from "socket.io-client";

import { useSession } from "@/services/auth0/session";

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
  connectionState: SocketConnectionState;
  lastConnectedAt: number | null;
  lastDisconnectedAt: number | null;
}

export type SocketConnectionState =
  | "connected"
  | "reconnecting"
  | "disconnected";

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  isConnected: false,
  connectionState: "disconnected",
  lastConnectedAt: null,
  lastDisconnectedAt: null,
});

const SOCKET_URL =
  import.meta.env.VITE_SOCKET_URL ??
  import.meta.env.VITE_API_BASE_URL ??
  "http://localhost:4000";

/**
 * Socket.io provider. Connects to ezx-server with the Auth0 access token in
 * the handshake auth payload. Server identifies the CarrierUser and joins the
 * appropriate `carrier:<id>` / `carrier:<id>:center:<id>` rooms.
 *
 * Skips connection entirely when no token is available (anonymous, pre-auth).
 */
export function SocketProvider({ children }: { children: ReactNode }) {
  const { token, isLoggedIn } = useSession();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] =
    useState<SocketConnectionState>("disconnected");
  const [lastConnectedAt, setLastConnectedAt] = useState<number | null>(null);
  const [lastDisconnectedAt, setLastDisconnectedAt] = useState<number | null>(
    null
  );
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!isLoggedIn || !token) {
      // No auth — never open a socket. Tear down any previous one.
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      setSocket(null);
      setIsConnected(false);
      setConnectionState("disconnected");
      return;
    }

    const s = io(SOCKET_URL, {
      auth: { token },
      transports: ["websocket", "polling"],
      reconnection: true,
    });

    socketRef.current = s;
    setSocket(s);
    setConnectionState("reconnecting");

    const onConnect = () => {
      setIsConnected(true);
      setConnectionState("connected");
      setLastConnectedAt(Date.now());
    };
    const onDisconnect = () => {
      setIsConnected(false);
      setConnectionState(s.active ? "reconnecting" : "disconnected");
      setLastDisconnectedAt(Date.now());
    };
    const onConnectError = (err: Error) => {
      // Don't crash UI if server is unreachable — log and let socket.io retry.
      console.warn("[socket] connect_error:", err.message);
      setIsConnected(false);
      setConnectionState(s.active ? "reconnecting" : "disconnected");
    };
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("connect_error", onConnectError);

    return () => {
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("connect_error", onConnectError);
      s.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
      setConnectionState("disconnected");
    };
  }, [token, isLoggedIn]);

  return (
    <SocketContext.Provider
      value={{
        socket,
        isConnected,
        connectionState,
        lastConnectedAt,
        lastDisconnectedAt,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  return useContext(SocketContext);
}
